import { type MenuId } from '../../../models';
import { type PersonalShortcuts } from '../models';

const baseUrl = process.env.REACT_APP_NAV_API ?? '';

interface PersonalizationResource {
  info: string;
}

interface PersonalizationData {
  shortcutIds: MenuId[] | null;
}

/**
 * Gets the user's personalization data. null means none exist.
 */
async function readPersonalizationData() {
  const response = await fetch(`${baseUrl}/ECMv6/api/idw/personalizations/1`, {
    headers: { 'Content-Type': 'application/json' },
  });

  // No personalizations exist, return null to defer to defaults.
  if (response.status === 404) {
    return null;
  }

  const { info: dataString } =
    (await response.json()) as PersonalizationResource;

  const etag = response.headers.get('etag');
  const personalizationData = JSON.parse(dataString) as PersonalizationData;

  return {
    etag,
    personalizationData,
  };
}

/**
 * Gets the user's shortcuts personalization.
 */
export async function readPersonalShortcuts(): Promise<PersonalShortcuts> {
  const data = await readPersonalizationData();

  if (data === null) {
    return {};
  }

  if (!data.etag) {
    return 'error';
  }

  // etag value might start with `W/`. either way, it will be wrapped in
  // double-quotes. need to strip everything but the value itself.
  let { etag } = data;

  if (etag.startsWith('W/"')) {
    // weak validator; remove the W/
    etag = etag.slice(2);
  }

  etag = etag.slice(1, -1);

  const { shortcutIds } = data.personalizationData;

  return {
    version: etag,
    shortcutIds,
  };
}

/**
 * Persists shortcuts to backend. If mid-air collision detected, return false.
 */
export async function trySavePersonalShortcuts(
  shortcutIds: MenuId[] | null,
  version: string | undefined
): Promise<boolean> {
  const data = await readPersonalizationData();
  const personalizationData: PersonalizationData = {
    ...data?.personalizationData,
    shortcutIds,
  };
  const headers: HeadersInit = { 'Content-Type': 'application/json' };

  if (version) {
    headers['If-Match'] = version;
  }

  const response = await fetch(`${baseUrl}/ECMv6/api/idw/personalizations/1`, {
    method: version ? 'PUT' : 'POST',
    headers,
    body: JSON.stringify(personalizationData),
  });

  return response.ok;
}
